import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, forkJoin, of } from 'rxjs';
import { MaterialModule } from '../material.module';
import { CalibrationTemplateItem, InstrumentCalibrationItem, LabEquipmentInstrument, LabEquipmentInstrumentRequest, LabEquipmentInstrumentType, LabEquipmentLocation, VendorCalibrationRequest } from './lab-equipment.models';
import { LabEquipmentService } from './lab-equipment.service';

export interface InstrumentFormDialogData {
  instrument: LabEquipmentInstrument | null;
  locations: LabEquipmentLocation[];
}

@Component({
  selector: 'app-instrument-form-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './instrument-form-dialog.component.html',
  styleUrl: './instrument-form-dialog.component.scss',
})
export class InstrumentFormDialogComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(LabEquipmentService);
  readonly dialogRef = inject(MatDialogRef<InstrumentFormDialogComponent>);
  readonly data = inject<InstrumentFormDialogData>(MAT_DIALOG_DATA);
  readonly form;
  isSaving = false;
  isLoadingTypes = true;
  validationAttempted = false;
  errorMessage = '';
  types: LabEquipmentInstrumentType[] = [];
  calibrationItems: CalibrationTemplateItem[] = [];
  assignments: InstrumentCalibrationItem[] = [];
  initialDueDateControls: Record<number, FormControl<string>> = {};
  calibrationEnabledControls: Record<number, FormControl<boolean>> = {};
  readonly vendorServiceTypes = ['Pressure Certification', 'Annual PM', 'Reservoir Cleanout', 'Transit Time Check', 'Annual Load Cell Check'];
  vendorConfigured = this.data.instrument?.vendorConfigurationEnabled ?? false;
  vendorServices: VendorCalibrationRequest[] = (this.data.instrument?.vendorCalibrations ?? [])
    .filter(service => service.isActive)
    .map(service => ({
      serviceType: service.serviceType,
      intervalMonths: service.intervalMonths,
      lastCompletedDate: service.lastCompletedDate,
      nextDueDate: service.nextDueDate,
    }));

  constructor() {
    const instrument = this.data.instrument;
    this.form = this.formBuilder.nonNullable.group({
      equipmentNumber: [instrument?.equipmentNumber ?? '', Validators.maxLength(50)],
      name: [instrument?.name ?? '', [Validators.required, Validators.maxLength(200)]],
      manufacturer: [instrument?.manufacturer ?? '', [Validators.required, Validators.maxLength(200)]],
      modelNumber: [instrument?.modelNumber ?? '', [Validators.required, Validators.maxLength(100)]],
      serialNumber: [instrument?.serialNumber ?? '', [Validators.required, Validators.maxLength(100)]],
      assetNumber: [instrument?.assetNumber ?? '', Validators.maxLength(100)],
      rating: [instrument?.rating ?? '', Validators.maxLength(200)],
      vendorCalibrationSchedule: [instrument?.vendorCalibrationSchedule ?? '', Validators.maxLength(100)],
      vendorCalibrationIntervalMonths: [instrument?.vendorCalibrationIntervalMonths ?? 12, [Validators.min(1), Validators.max(120)]],
      vendorLastCompletedDate: [instrument?.vendorLastCompletedDate ?? ''],
      vendorNextDueDate: [instrument?.vendorNextDueDate ?? ''],
      locationId: [instrument?.location.id ?? 0, [Validators.required, Validators.min(1)]],
      instrumentTypeId: new FormControl<number | null>(instrument?.instrumentTypeId ?? null),
    });
  }

  ngOnInit(): void {
    forkJoin({
      types: this.service.getInstrumentTypes(),
      items: this.service.getCalibrationTemplateItems(),
      assignments: this.data.instrument
        ? this.service.getInstrumentCalibrationItems(this.data.instrument.id)
        : of([] as InstrumentCalibrationItem[]),
    }).pipe(finalize(() => this.isLoadingTypes = false)).subscribe({
      next: ({ types, items, assignments }) => {
        this.types = types;
        this.calibrationItems = items;
        this.assignments = assignments;
        if (!this.data.instrument && types.some(type => type.isActive)) {
          this.form.controls.instrumentTypeId.addValidators(Validators.required);
          this.form.controls.instrumentTypeId.updateValueAndValidity();
        }
        for (const item of items) {
          const assignment = assignments.find(candidate => candidate.calibrationTemplateItemId === item.id);
          const isEnabled = assignment?.isActive ?? true;
          this.calibrationEnabledControls[item.id] = new FormControl(isEnabled, { nonNullable: true });
          this.initialDueDateControls[item.id] = new FormControl(assignment?.initialDueDate ?? '', {
            nonNullable: true,
            validators: isEnabled && this.requiresInitialDueDate(item.id) ? Validators.required : [],
          });
        }
      },
      error: () => this.errorMessage = 'Equipment types could not be loaded.',
    });
  }

  get selectedTypeItems(): CalibrationTemplateItem[] {
    const selectedType = this.types.find(type => type.id === this.form.controls.instrumentTypeId.value);
    if (!selectedType) return [];
    return this.calibrationItems.filter(item => selectedType.calibrationTemplateItemIds.includes(item.id));
  }

  requiresInitialDueDate(itemId: number): boolean {
    const assignment = this.assignments.find(candidate => candidate.calibrationTemplateItemId === itemId);
    return !assignment || (!assignment.initialDueDate && !assignment.lastCompletedDate);
  }

  updateCalibrationState(itemId: number): void {
    const dateControl = this.initialDueDateControls[itemId];
    if (!dateControl) return;
    dateControl.setValidators(this.calibrationEnabledControls[itemId].value && this.requiresInitialDueDate(itemId)
      ? Validators.required
      : []);
    dateControl.updateValueAndValidity();
  }

  selectInstrumentType(): void {
    if (this.data.instrument || this.vendorConfigured) return;
    const type = this.types.find(candidate => candidate.id === this.form.controls.instrumentTypeId.value);
    if (!type) return;
    this.vendorConfigured = true;
    this.vendorServices = (type.vendorDefaults ?? [{ serviceType: 'Annual PM', intervalMonths: 12 }])
      .map(service => ({ ...service, lastCompletedDate: null, nextDueDate: null }));
  }

  addVendorService(): void {
    this.vendorConfigured = true;
    this.vendorServices.push({ serviceType: '', intervalMonths: 0, lastCompletedDate: null, nextDueDate: null });
  }

  removeVendorService(index: number): void {
    this.vendorServices.splice(index, 1);
  }

  proposeServiceDueDate(service: VendorCalibrationRequest): void {
    if (!service.lastCompletedDate || !service.intervalMonths) return;
    const [year, month, day] = service.lastCompletedDate.split('-').map(Number);
    const firstOfDueMonth = new Date(Date.UTC(year, month - 1 + service.intervalMonths, 1));
    const lastDay = new Date(Date.UTC(firstOfDueMonth.getUTCFullYear(), firstOfDueMonth.getUTCMonth() + 1, 0)).getUTCDate();
    service.nextDueDate = new Date(Date.UTC(firstOfDueMonth.getUTCFullYear(), firstOfDueMonth.getUTCMonth(), Math.min(day, lastDay)))
      .toISOString().slice(0, 10);
  }

  save(): void {
    if (this.isSaving || this.isLoadingTypes) return;

    this.validationAttempted = true;
    this.form.markAllAsTouched();
    const selectedTypeItems = this.selectedTypeItems;
    for (const item of selectedTypeItems) {
      this.initialDueDateControls[item.id]?.markAsTouched();
    }
    const hasMissingInitialDueDate = selectedTypeItems.some(item => this.initialDueDateControls[item.id]?.invalid);
    if ((this.types.some(type => type.isActive) && !this.form.controls.instrumentTypeId.value && !this.data.instrument)
        || hasMissingInitialDueDate) {
      this.errorMessage = 'Choose an equipment type and enter the first due date for each new calibration.';
    }

    if (this.form.invalid || hasMissingInitialDueDate) {
      return;
    }

    const names = this.vendorServices.map(service => service.serviceType);
    if (this.vendorServices.some(service => !this.vendorServiceTypes.includes(service.serviceType)
        || !Number.isInteger(service.intervalMonths) || service.intervalMonths < 1 || service.intervalMonths > 120)
        || new Set(names).size !== names.length) {
      this.errorMessage = 'Choose a unique Vendor service and interval in months for each row.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    const value = this.form.getRawValue();
    const request: LabEquipmentInstrumentRequest = {
      equipmentNumber: this.optional(value.equipmentNumber),
      name: value.name.trim(),
      manufacturer: value.manufacturer.trim(),
      modelNumber: value.modelNumber.trim(),
      serialNumber: value.serialNumber.trim(),
      assetNumber: this.optional(value.assetNumber),
      rating: this.optional(value.rating),
      vendorCalibrationSchedule: this.optional(value.vendorCalibrationSchedule),
      vendorCalibrationIntervalMonths: value.vendorCalibrationSchedule.trim().toUpperCase() === 'N/A'
        ? null
        : value.vendorCalibrationIntervalMonths,
      vendorLastCompletedDate: value.vendorLastCompletedDate || null,
      vendorNextDueDate: value.vendorNextDueDate || null,
      locationId: value.locationId,
      instrumentTypeId: value.instrumentTypeId,
      initialDueDates: selectedTypeItems.map(item => ({
        calibrationTemplateItemId: item.id,
        initialDueDate: this.initialDueDateControls[item.id].value || null,
        isActive: this.calibrationEnabledControls[item.id].value,
      })),
      vendorCalibrations: this.vendorConfigured ? this.vendorServices.map(service => ({
        serviceType: service.serviceType,
        intervalMonths: service.intervalMonths,
        lastCompletedDate: service.lastCompletedDate || null,
        nextDueDate: service.nextDueDate || null,
      })) : null,
    };

    const operation = this.data.instrument
      ? this.service.updateInstrument(this.data.instrument.id, {
          ...request,
          rowVersion: this.data.instrument.rowVersion,
        })
      : this.service.createInstrument(request);

    operation.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: instrument => this.dialogRef.close(instrument),
      error: error => this.errorMessage = this.describeError(error),
    });
  }

  proposeVendorDueDate(): void {
    const lastCompleted = this.form.controls.vendorLastCompletedDate.value;
    const months = this.form.controls.vendorCalibrationIntervalMonths.value;
    if (!lastCompleted || !months) return;
    const [year, month, day] = lastCompleted.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1 + months, day));
    this.form.controls.vendorNextDueDate.setValue(date.toISOString().slice(0, 10));
  }

  openDatePicker(input: HTMLInputElement): void {
    input.showPicker?.();
  }

  private optional(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  private describeError(error: unknown): string {
    const apiError = error as { status?: number; error?: { detail?: string; errors?: Record<string, string[]> } };
    if (apiError.status === 409) {
      return apiError.error?.detail ?? 'An instrument with this model and serial number already exists, or the record changed.';
    }
    const validationErrors = apiError.error?.errors;
    if (validationErrors) {
      return Object.values(validationErrors).flat().join(' ');
    }
    return 'The instrument could not be saved. Please try again.';
  }
}
